import React, { useState, useEffect, useMemo } from "react";

/* ================= HELPERS ================= */
const isValidBtcAddress = (addr) =>
  /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,}$/i.test(addr);

const toBtc8 = (v) => Number(v || 0).toFixed(8);


export default function Send({ wallet, btc, onBack }) {
  /* ================= STATE ================= */
  const [toAddress, setToAddress] = useState("");

  const [amount, setAmount] = useState(""); // BTC (source of truth)
  const [amountBtc, setAmountBtc] = useState("");
  const [amountUsdInput, setAmountUsdInput] = useState("");
  const [lastEdited, setLastEdited] = useState(null);

  const [speed, setSpeed] = useState("normal");
  const [feeInfo, setFeeInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [rbf, setRbf] = useState(false);

  const btcUsd = wallet?.btcUsd || 0;

  /* ================= DERIVED BALANCE ================= */
  const available =
    wallet?.available ??
    Math.max(
      (wallet?.confirmedBalance ?? 0) - (wallet?.committed ?? 0),
      0
    );

  /* =====================================================
     BTC ⇄ USD SYNC
  ===================================================== */
  useEffect(() => {
    if (!btcUsd) return;

    if (lastEdited === "btc") {
      const btcVal = Number(amountBtc);
      if (!btcVal || btcVal <= 0) {
        setAmountUsdInput("");
        setAmount("");
        return;
      }
      setAmountUsdInput((btcVal * btcUsd).toFixed(2));
      setAmount(btcVal.toFixed(8));
    }

    if (lastEdited === "usd") {
      const usdVal = Number(amountUsdInput);
      if (!usdVal || usdVal <= 0) {
        setAmountBtc("");
        setAmount("");
        return;
      }
      const btcVal = usdVal / btcUsd;
      setAmountBtc(btcVal.toFixed(8));
      setAmount(btcVal.toFixed(8));
    }
  }, [amountBtc, amountUsdInput, lastEdited, btcUsd]);

  /* =====================================================
     AUTO FEE PREVIEW
  ===================================================== */
  useEffect(() => {
    if (!wallet?.chatId) return;

    setError("");

    const value = Number(amount);
    if (!value || value <= 0) {
      setFeeInfo(null);
      return;
    }

    const timer = setTimeout(() => {
      btc.previewFee(
        { chatId: wallet.chatId, amount, speed },
        (res) => {
          if (!res?.success) {
            setFeeInfo(null);
            setError(res?.error || "Fee preview failed");
          } else {
            setFeeInfo(res);
          }
        }
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [amount, speed, wallet?.chatId]);

  /* =====================================================
     VALIDATION
  ===================================================== */
  const amountNum = Number(amount);
  const hasValidAmount = amountNum > 0;
  const hasValidAddress = isValidBtcAddress(toAddress);

  const canSubmit = useMemo(
    () =>
      !sending &&
      hasValidAddress &&
      hasValidAmount &&
      feeInfo?.canSend === true,
    [sending, hasValidAddress, hasValidAmount, feeInfo]
  );

  /* =====================================================
     MAX BUTTON (✅ USES AVAILABLE)
  ===================================================== */
  const handleMax = () => {
  if (!available || available <= 0) return;

  // subtract fee if preview already exists
  const fee = Number(feeInfo?.fee || 0);
  const spendable = Math.max(available - fee, 0);

  setLastEdited("btc");
  setAmountBtc(toBtc8(spendable));
};

  /* ================= SEND BTC ================= */
  const sendBtc = () => {
    if (!canSubmit) return;

    setSending(true);
    setError("");

    btc.sendBtc(
      { chatId: wallet.chatId, toAddress, amount, speed, rbf },
      (res) => {
        setSending(false);

        if (!res?.success) {
          setError(res?.error || "BTC send failed");
          return;
        }

        setToAddress("");
        setAmount("");
        setAmountBtc("");
        setAmountUsdInput("");
        setFeeInfo(null);

        alert(`✅ BTC sent successfully\n\nTX:\n${res.txHash}`);
        onBack?.();
      }
    );
  };

  /* ================= RENDER ================= */
  return (
    <div className="h-full w-full bg-neutral-50 dark:bg-neutral-800 flex">
      <div className="flex-1" />

      <div className="flex h-full w-full max-w-2xl flex-col px-6 py-6">
        <button
          onClick={onBack}
          className="mb-4 text-sm font-medium text-orange-500 hover:underline"
        >
          ← Back to Assets
        </button>

        <div className="flex flex-col gap-4 rounded-2xl border shadow bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 p-6 flex-1">
          <h3 className="text-lg font-semibold text-orange-500">
            Send Bitcoin
          </h3>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-orange-500 mb-1">
              Recipient address
            </label>
            <input
              className="w-full rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="bc1q..."
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value.trim())}
            />
            {toAddress && !hasValidAddress && (
              <div className="text-xs text-red-600 mt-1">
                Invalid BTC address
              </div>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-orange-500 mb-2">
              Amount
            </label>

            <div className="space-y-2">
              {/* BTC */}
              <div className="flex items-center gap-3">
                <span className="w-14 text-sm font-medium text-white bg-orange-500 rounded-md px-2 py-1 text-center">
                  BTC
                </span>

                <input
                  type="number"
                  step="0.00000001"
                  value={amountBtc}
                  onChange={(e) => {
  setLastEdited("btc");

  const raw = e.target.value;
  if (raw === "") {
    setAmountBtc("");
    setError("");
    return;
  }

  const n = Number(raw);
  const fee = Number(feeInfo?.fee || 0);
  const maxSpendable = Math.max(available - fee, 0);

  if (n > maxSpendable) {
    setAmountBtc(toBtc8(maxSpendable));
    setError(
      `You can send up to ${toBtc8(maxSpendable)} BTC after fees`
    );
    return;
  }

  setError("");
  setAmountBtc(raw);
}}

                  className="flex-1 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 text-neutral-900 dark:text-white"
                />

                <button
                  type="button"
                  onClick={handleMax}
                  className="rounded-md px-3 py-1 text-xs font-semibold bg-green-600 text-white hover:bg-green-700"
                >
                  MAX
                </button>
              </div>

              {/* USD */}
              <div className="flex items-center gap-3">
                <span className="w-14 text-sm font-medium text-white bg-orange-500 rounded-md px-2 py-1 text-center">
                  USD
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={amountUsdInput}
                 onChange={(e) => {
  setLastEdited("usd");

  const raw = e.target.value;
  if (raw === "") {
    setAmountUsdInput("");
    setError("");
    return;
  }

  const usdVal = Number(raw);
  if (!usdVal || usdVal <= 0) {
    setAmountUsdInput(raw);
    setError("");
    return;
  }

  const feeBtc = Number(feeInfo?.fee || 0);
  const maxSpendableBtc = Math.max(available - feeBtc, 0);
  const maxUsd = maxSpendableBtc * btcUsd;

  if (usdVal > maxUsd) {
    setAmountUsdInput(maxUsd.toFixed(2));
    setError(
      `You can send up to $${maxUsd.toFixed(2)} after network fees`
    );
    return;
  }

  setError("");
  setAmountUsdInput(raw);
}}

                  className="flex-1 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 text-neutral-900 dark:text-white"
                />
              </div>
            </div>

            {!hasValidAmount && (
              <div className="text-xs text-red-600 mt-1">
                Amount must be greater than 0
              </div>
            )}
          </div>

          {/* Speed */}
          <div>
            <label className="block text-sm font-medium text-orange-500 mb-1">
              Transaction speed
            </label>
            <select
              value={speed}
              onChange={(e) => setSpeed(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 text-neutral-900 dark:text-white"
            >
              <option value="normal">Normal fee</option>
              <option value="fast">Fast fee</option>
            </select>
          </div>

          {/* RBF */}
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={rbf}
              onChange={(e) => setRbf(e.target.checked)}
              className="mt-1 accent-green-500"
            />
            <span className="text-neutral-700 dark:text-neutral-300">
              <span className="font-medium text-green-500">
                🔁 Replace-By-Fee
              </span>
              <div className="text-xs text-neutral-500">
                {rbf
                  ? "Enabled — you can speed up the transaction later"
                  : "Disabled — fee cannot be increased after sending"}
              </div>
            </span>
          </label>

          {/* Fee Preview */}
          {feeInfo && (
            <div className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-900/20 p-3 text-sm">
              <div className="text-green-600 dark:text-green-400">
                Fee: <strong>{feeInfo.fee}</strong> BTC
              </div>
              <div className="text-green-700 dark:text-green-400">
                Total: <strong>{feeInfo.totalRequired}</strong> BTC
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-2 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* CTA */}
          <div className="mt-auto">
            <button
              disabled={!canSubmit}
              onClick={sendBtc}
              className={`w-full rounded-xl py-3 text-sm font-semibold transition ${
                canSubmit
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-neutral-300 text-neutral-600 cursor-not-allowed"
              }`}
            >
              {sending ? "Sending…" : "Send BTC"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1" />
    </div>
  );
}
